// CÓMO TERMINA EL MES — una sola proyección, y con su margen a la vista.
//
// ── Por qué hay una sola ─────────────────────────────────────────────────
//
// Hoy hay DOS, en dos pestañas distintas, y dan números diferentes:
//
//   TabMensual.js:258   llevado + promedioDeDíasCompletos × (díasDelMes − hoy)
//   TabResumen.js:112   (ingresoDelMes ÷ documentosQueExisten) × díasDelMes
//
// La segunda divide por los días que TIENEN DOCUMENTO, no por los transcurridos:
// si la playa cerró cuatro días, el divisor baja, el promedio sube y la
// proyección se infla. Dos números distintos con el mismo nombre, a dos
// pestañas de distancia.
//
// ── Por qué ESTA fórmula, que es la más tonta ────────────────────────────
//
// El primer intento proyectaba cada día que falta con la mediana de ESE día de
// la semana. Suena mejor y es peor: medido contra 90 casos reales de un cliente
// con dos años y medio de historia, dio 20,4% de error mediano contra 17,5% de
// la regla de tres. Menos muestras por día de semana = más ruido, y el ruido se
// come la ganancia de precisión que la idea prometía.
//
//   regla de tres     17,5%   ← ésta
//   mediana del mes   18,0%
//   promedio del mes  19,1%
//   por día de semana 20,4%
//   = al mes anterior 47,6%
//
// ── Por qué se muestra un RANGO y no un número ───────────────────────────
//
// Ninguna de las cinco baja del 17% a mitad de mes. Un "vas a terminar en
// $6.900.000" con 20% de error real es exactamente la clase de precisión
// fingida que esta pantalla existe para eliminar. El margen no se inventa: se
// calibra con la propia historia del cliente, preguntándole a cada mes pasado
// cuánto se equivocaba esta misma fórmula parada en este mismo día.
//
// Verificado sobre 129 casos reales — el mes real cae DENTRO del rango:
//
//   día 10   84%    (rango de 65% de ancho)
//   día 15   72%    (48%)
//   día 20   70%    (30%)
//   día 25   81%    (26%)
//   total    80%
//
// Y la comparación contra el mes pasado sólo se AFIRMA cuando el rango entero
// cae de un lado. Si el mes pasado queda adentro del margen, la respuesta
// honesta es "parecido" — no un porcentaje que finge saber más de lo que sabe.

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const percentil = (nums, p) => {
  const o = nums.filter((n) => typeof n === "number" && isFinite(n)).sort((a, b) => a - b);
  if (!o.length) return null;
  return o[Math.min(o.length - 1, Math.floor(p * o.length))];
};

const diasDelMesDe = (anio, mesIdx) => new Date(anio, mesIdx + 1, 0).getDate();

const P_BAJO = 0.1;
const P_ALTO = 0.9;

/**
 * La fórmula. Se escribe una vez y se usa también para calibrar el margen, así
 * que el margen mide el error de LO QUE SE MUESTRA y no el de otra cosa.
 */
const estimar = (llevado, diaDelMes, diasDelMes) =>
  diaDelMes > 0 ? llevado * (diasDelMes / diaDelMes) : llevado;

/**
 * Calibra el margen con la historia del cliente.
 *
 * Para cada mes cerrado se pregunta: parado en este mismo día del mes, ¿qué
 * habría dicho la fórmula, y en cuánto terminó? La dispersión de esos errores
 * ES el margen — de este cliente, no de un promedio de la industria.
 */
const calibrarMargen = (cerrados, diaDelMes) => {
  const porMes = new Map();
  cerrados.forEach((d) => {
    if (!d.date) return;
    const m = d.date.slice(0, 7);
    if (!porMes.has(m)) porMes.set(m, []);
    porMes.get(m).push(d);
  });

  const ratios = [];
  for (const [mes, dias] of porMes) {
    const total = dias.reduce((a, d) => a + (d.income || 0), 0);
    if (total <= 0) continue;
    const [anio, mm] = mes.split("-").map(Number);
    const largo = diasDelMesDe(anio, mm - 1);
    // Sólo meses de los que tenemos casi todo: con la mitad de los días
    // faltando, el "real" contra el que compararíamos ya sería falso.
    if (dias.length < largo - 3) continue;
    const hasta = dias
      .filter((d) => Number(d.date.slice(8, 10)) <= diaDelMes)
      .reduce((a, d) => a + (d.income || 0), 0);
    if (hasta <= 0) continue;
    ratios.push(estimar(hasta, diaDelMes, largo) / total);
  }

  if (ratios.length < 3) return null;
  // Los percentiles del margen. Medido sobre 129 casos reales de un cliente con
  // dos años y medio de historia, p20–p80 dejaba al mes real FUERA del rango el
  // 39% de las veces. Un rango que se equivoca 4 de cada 10 no se puede
  // mostrar: la próxima vez el dueño ya no lo mira.
  const bajo = percentil(ratios, P_BAJO);
  const alto = percentil(ratios, P_ALTO);
  return { bajo, alto, meses: ratios.length };
};

/**
 * @param {Array}  cerrados  días cerrados, ascendente, con `date` e `income`
 * @param {Object} hoyDoc    documento del día en curso (puede ser null)
 * @param {string} hoyISO    fecha de hoy, en local
 * @param {Array}  mensuales documentos mensuales, para el mes anterior
 */
export function proyeccionMes(cerrados = [], hoyDoc = null, hoyISO = null, mensuales = []) {
  const hoy = hoyISO ? new Date(`${hoyISO}T00:00:00`) : new Date();
  const mesActual = iso(hoy).slice(0, 7);
  const diasDelMes = diasDelMesDe(hoy.getFullYear(), hoy.getMonth());
  const diaDeHoy = hoy.getDate();

  const delMes = cerrados.filter((d) => d.date?.startsWith(mesActual));
  const llevas = delMes.reduce((a, d) => a + (d.income || 0), 0) + (hoyDoc?.income || 0);

  // Días con documento, que NO es lo mismo que días transcurridos. Confundirlos
  // es lo que hace que un "30d" sean 35 días de calendario sin que nadie avise.
  const diasOperados = delMes.length + (hoyDoc ? 1 : 0);

  const terminado = diaDeHoy >= diasDelMes;
  const central = terminado ? llevas : estimar(llevas, diaDeHoy, diasDelMes);

  // Antes del día 10 NO se proyecta, y no es un número elegido a ojo. Medido
  // sobre la historia real: parado el día 7 el margen mide 103% de ancho — o
  // sea "vas a terminar entre X y el doble de X", que el dueño ya sabía. Recién
  // desde el 10 el rango dice algo (65% de ancho) y desde el 20 es fino (30%).
  const proyectable = !terminado && diaDeHoy >= 10 && llevas > 0;

  let banda = null;
  if (proyectable) {
    const cal = calibrarMargen(cerrados.filter((d) => !d.date?.startsWith(mesActual)), diaDeHoy);
    if (cal) {
      // El ratio dice cuánto SOBRESTIMA la fórmula, así que se divide.
      banda = {
        bajo: central / cal.alto,
        alto: central / cal.bajo,
        meses: cal.meses,
      };
    }
  }

  // ── El mes anterior ────────────────────────────────────────────────────
  const anterior = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  const claveAnterior = iso(anterior).slice(0, 7);
  let mesPasado = mensuales.find((m) => m.month === claveAnterior)?.income ?? null;
  let mesPasadoParcial = false;
  if (mesPasado === null) {
    // El Desktop consolida el documento mensual entre el día 1 y el 3. En el
    // medio se suman los diarios — y se rotula, porque puede faltar alguno.
    const diarios = cerrados.filter((d) => d.date?.startsWith(claveAnterior));
    if (diarios.length) {
      mesPasado = diarios.reduce((a, d) => a + (d.income || 0), 0);
      mesPasadoParcial = true;
    }
  }

  // La comparación contra el mes pasado sólo se afirma si la BANDA ENTERA cae
  // de un lado. Si el mes pasado está adentro del margen, la respuesta honesta
  // es "parecido", no un porcentaje con dos decimales.
  let contraMesPasado = null;
  if (mesPasado > 0) {
    if (terminado) {
      contraMesPasado = llevas > mesPasado ? "arriba" : llevas < mesPasado ? "abajo" : "igual";
    } else if (banda) {
      contraMesPasado = banda.bajo > mesPasado ? "arriba" : banda.alto < mesPasado ? "abajo" : "parecido";
    }
  }

  return {
    llevas,
    diasOperados,
    diaDeHoy,
    diasDelMes,
    terminado,
    proyectable,
    central: proyectable || terminado ? central : null,
    banda,                 // { bajo, alto, meses } o null si no se pudo calibrar
    mesPasado,
    mesPasadoParcial,
    contraMesPasado,       // 'arriba' | 'abajo' | 'parecido' | 'igual' | null
    variacionCentral: mesPasado > 0 ? ((central - mesPasado) / mesPasado) * 100 : null,
  };
}

// ── Gastos fijos: se quitaron ────────────────────────────────────────────
//
// Vivían acá `leerCostoFijo`, `guardarCostoFijo` y `diaQueCubre`: el dueño
// cargaba cuánto le cuesta el mes y la pantalla le decía qué día lo cubrió.
//
// La idea sigue siendo buena —"cubriste tus gastos el día 14" es un número que
// hoy no tiene en ningún lado— pero aquella versión no se la ganaba: era lo
// único de la pantalla que PEDÍA antes de dar, y se guardaba sólo en ese
// navegador, así que lo que cargabas en la PC no aparecía en el teléfono.
//
// Cuando vuelva, el costo se va a guardar junto a la configuración del
// estacionamiento y va a viajar con el cliente, así que los tres helpers de
// `localStorage` no se reusarían. Se borran en vez de dejarlos muertos.
